import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Store } from '@ngrx/store';
import { combineLatest, firstValueFrom } from 'rxjs';
import { filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { FolderEntity, FolderTypes } from '../dave-data-module/entities/folder.entity';
import { ProcessTemplateEntity } from '../dave-data-module/entities/process-template.entity';
import { ProcessEntity, ProcessState } from '../dave-data-module/entities/process.entity';
import { ProcessTemplateResolver } from '../dave-data-module/guards/process-template.resolver';
import { ProcessResolver } from '../dave-data-module/guards/process.resolver';
import { FolderDataService } from '../dave-data-module/services/folder-data.service';
import { State } from '../dave-data-module/State';
import { getProcessTemplateActive, getProcessTemplateFetched } from '../dave-data-module/State/selectors/process-template.selector';
import {
    getProcessesActive,
    getProcessFetched,
} from '../dave-data-module/State/selectors/process.selector';
import { DetailListDialogReturn, DetailListTemplateDialogComponent, DetailListTemplateDialogData } from '../detail-list-template-dialog/components/detail-list-template-dialog.component';
import { sortByDateProperty } from '../helper/helper';
import { FilesActionTypes } from '../dave-data-module/State/actions/files.actions';
import {
    AppConfirmationDialogComponent,
    AppConfirmationDialogData, AppConfirmationDialogReturnData,
} from '../dave-utils-module/app-dialog-module/app-confirmation-dialog/app-confirmation-dialog.component';
import { FileDataService } from '../dave-data-module/services/file-data.service';
import { FileEntity } from '../dave-data-module/entities/file.entity';

@Injectable({
    providedIn: 'root',
})
export class ProcessService {
    constructor(
        private processTemplateResolver: ProcessTemplateResolver,
        private processResolver: ProcessResolver,
        private dialog: MatDialog,
        private store: Store<State>,
        private folderDataService: FolderDataService,
        private fileDataService: FileDataService,
        private confirmationDialog: MatDialog,
    ) {}

    async openAddFileToProcessDialog(fileIds: number[]) {
        // Lade Process Templates und Processes
        const processTemplates = await this.getProcessTemplates();
        const processes = await this.getProcesses();

        // Form Setup
        const form = new FormGroup({
            processTemplate: new FormControl<{ Id: number }>(null),
            process: new FormControl<{ Id: number }>(null, Validators.required),
        });

        // Dialog zur Auswahl eines Prozesses öffnen
        const dialogRef = await this.openDetailsListDialog(processTemplates, processes, form);
        const dialogResult = await firstValueFrom(dialogRef.afterClosed());

        // Prüfe, ob ein Prozess ausgewählt und gespeichert wurde
        if (dialogResult?.Action !== 'save') return;

        const inProcessFolder = await this.areSelectedFilesInProcessFolder(fileIds);

        // Ordner des ausgewählten Prozesses abrufen
        const destFolder = await firstValueFrom(this.folderDataService.getFolderFromEntity(form.value.process.Id, FolderTypes.process));

        // File nicht in einem Prozessordner: Standard-Logik und Erstellung von Verknüpfungen
        if (!inProcessFolder) {
            await this.createFileLinks(fileIds, destFolder.Id);
            return;
        }

        // Wenn die Dateien bereits in einem Prozessordner sind, frage den Nutzer, ob sie verschoben oder verknüpft werden sollen
        const confirmationDialogResponse = await this.getConfirmationDialogResponse();

        if (!confirmationDialogResponse) {
            return; // Dialog wurde abgebrochen
        }

        if (confirmationDialogResponse[0]) {
            await this.moveFiles(fileIds, destFolder.Id);
        } else {
            // Verknüpfung soll weiterhin erstellt werden
            await this.createFileLinks(fileIds, destFolder.Id);
        }
    }

    private async getProcessTemplates(): Promise<ProcessTemplateEntity[]> {
        return firstValueFrom(
            this.store.select(getProcessTemplateFetched).pipe(
                tap((fetched) => {
                    if (!fetched) {
                        this.processTemplateResolver.resolve();
                    }
                }),
                filter((fetched) => fetched),
                switchMap(() => this.store.select(getProcessTemplateActive))
            )
        );
    }

    private async getProcesses(): Promise<ProcessEntity[]> {
        return firstValueFrom(
            this.store.select(getProcessFetched).pipe(
                tap((fetched) => {
                    if (!fetched) {
                        this.processResolver.resolve();
                    }
                }),
                filter((fetched) => fetched),
                switchMap(() => this.store.select(getProcessesActive))
            )
        );
    }

    private async openDetailsListDialog(processTemplates: ProcessTemplateEntity[], processes: ProcessEntity[], form: FormGroup) {
        return this.dialog.open<DetailListTemplateDialogComponent, DetailListTemplateDialogData, DetailListDialogReturn>(
            DetailListTemplateDialogComponent,
            {
                ...DetailListTemplateDialogComponent.DefaultConfig,
                data: {
                    DisableSaveButton$: form.statusChanges.pipe(
                        startWith(form.status),
                        map((state) => state !== 'VALID'),
                    ),
                    Editing: true,
                    Data: {
                        Headline: 'Zu Prozess hinzufügen',
                        Properties: [
                            {
                                key: ProcessTemplateEntity.EntityName,
                                formControl: form.controls.processTemplate,
                                options: {
                                    specialInput: {
                                        singleSelectSearch: {
                                            options: processTemplates.map((t) => ({
                                                Id: t.Id,
                                                optionLabel: t.DisplayName,
                                            })),
                                            compareOptions: (a, b) => a.Id === b.Id,
                                        },
                                    },
                                },
                            },
                            {
                                key: ProcessEntity.EntityName,
                                formControl: form.controls.process,
                                options: {
                                    specialInput: {
                                        singleSelectSearch: {
                                            compareOptions: (a, b) => a.Id === b.Id,
                                            options$: form.controls.processTemplate.valueChanges.pipe(
                                                startWith(form.controls.processTemplate.value),
                                                map((template) => this.filterProcesses(processes, template)),
                                            ),
                                        },
                                    },
                                },
                            },
                        ],
                    },
                },
            }
        );
    }

    private filterProcesses(processes: ProcessEntity[], template: { Id: number }) {
        return processes
            .filter((p: ProcessEntity) => !template || p.ProcessTemplateId === template?.Id)
            .filter((p: ProcessEntity) => p.State !== ProcessState.ProcessStateArchived)
            .filter((p: ProcessEntity) => p.State !== ProcessState.ProcessStateCanceled)
            .filter((p: ProcessEntity) => p.State !== ProcessState.ProcessStateDone)
            .sort(sortByDateProperty('CreatedAt'))
            .reverse()
            .map((p) => ({
                Id: p.Id,
                optionLabel: p.DisplayName,
            }));
    }

    /*
     * Verschiebt die angegebenen Dateien in den Zielordner des ausgewählten Prozesses.
     */
    private async moveFiles(fileIds: number[], destFolderId: number) {
        fileIds.forEach((fId) =>
            this.store.dispatch(FilesActionTypes.ModifyFileRequest({
                Payload: {
                    DocumentId: fId.toString(),
                    FolderId: destFolderId.toString(),
                    Hidden: false
                }
            })),
        );
    }

    /*
     * Erstellt Verknüpfungen für die angegebenen Dateien im Zielordner des ausgewählten Prozesses.
     */
    private async createFileLinks(fileIds: number[], destFolderId: number) {
        fileIds.forEach((id) => {
            this.store.dispatch(
                FilesActionTypes.CreateLinkRequest({
                    Payload: {
                        DocumentId: id.toString(),
                        FolderId: destFolderId.toString(),
                    },
                }),
            );
        });
    }

    /*
     * Öffnet einen Bestätigungsdialog, um zu fragen, ob das Dokument in den neuen Prozess verschoben werden soll.
     */
    private getConfirmationDialogResponse() {
        return firstValueFrom(
            this.confirmationDialog.open<AppConfirmationDialogComponent, AppConfirmationDialogData, AppConfirmationDialogReturnData>(
                AppConfirmationDialogComponent,
                {
                    data: {
                        heading: 'Wollen Sie die ausgewählten Dateien auch verschieben?',
                        paragraph: 'Wenn Sie "Nein" drücken, bleiben die ausgewählten Dateien auch in diesem Prozess erhalten',
                        yesButton: 'Ja',
                        noButton: 'Nein',
                        styleDelete: false,
                    },
                },
            ).afterClosed());
    }

    /*
     * Überprüft, ob alle ausgewählten Dateien in einem Prozessordner liegen.
     * @param fileIds - die IDs der Dateien, die überprüft werden sollen.
     */
    async areSelectedFilesInProcessFolder(fileIds: number[]): Promise<boolean> {
        console.log("FileIDS: ", fileIds);
        const fileAndFolderInformation = await this.getFileAndFolderInformation(fileIds);
        console.log("File and Folder Infos: ", fileAndFolderInformation);

        return fileAndFolderInformation.length > 0 && await Promise.all(
            fileAndFolderInformation.map(async (info) =>
                info.sourceFolder != null && await this.isInProcessFolder(info.sourceFolder.Id)
            )
        ).then(results => results.every(Boolean));
    }

    /*
     * Überprüft, ob ein Ordner oder einer seiner übergeordneten Ordner ein Prozessordner ist.
     * @param folderId - die ID des Ordners, der überprüft werden soll.
     */
    async isInProcessFolder(folderId: number): Promise<boolean> {
        const folders = await firstValueFrom(this.folderDataService.getFolderAndParentsById(folderId));
        console.log("Parent and current Folders: ", folders);
        return folders?.some(folder => folder.Type === FolderTypes.process) ?? false;
    }

    /*
    * Holt die Datei- und Ordnerinformationen für die angegebenen File Ids.
    * @param fileIds - die IDs der Dateien, für die Informationen abgerufen werden sollen.
    * @return ein Array von Objekten, die die Datei und den zugehörigen Ordner enthalten.
     */
    private async getFileAndFolderInformation(fileIds: number[]): Promise<{ file: FileEntity, sourceFolder: FolderEntity | null }[]> {
        const results = [];

        for (const fileId of fileIds) {
            const file = await firstValueFrom(this.fileDataService.GetFileById$(fileId));
            if (file) {
                // Hole den Ordner, in dem die Datei gespeichert ist
                let folder = null;
                if (file.FolderId) {
                    folder = await firstValueFrom(this.folderDataService.getFolderById(file.FolderId));
                }
                results.push({
                    file,
                    sourceFolder: folder
                });
            }
        }
        return results;
    }
}
